import { z } from "zod";

/**
 * Shared DTO cho Admin Plan Catalog & Group Subscription (US-AE14.F1-0046 · @UC9130 plan-catalog ·
 * @UC9132 entitlement). MỘT nguồn sự thật cho api (S01), admin web (S03).
 *
 * Monetization placeholder POST-MVP. Plan = danh mục gói; GroupSubscription = 1 nhóm ↔ 1 gói.
 *
 * BẤT BIẾN (invariants):
 * - MONEY no-float: `priceMinor` = BigInt minor units → STRING trên wire (regex chỉ chữ số). KHÔNG
 *   number JS (mất chính xác BigInt). Convention khớp admin-ai-cost/analytics.
 * - AC4 tách ledger TUYỆT ĐỐI: DTO billing KHÔNG có field bút toán/ledger. Entitlement gate = soft-block
 *   (breached + upgradeCta) — KHÔNG xoá/sửa data.
 * - `.strict()` chống over-post. Giá thật do PO nhập (priceMinor "0" = PENDING — KHÔNG bịa).
 */

// ============================================================
// Enum-lite
// ============================================================

/** Chu kỳ tính phí. */
export const billingCycleSchema = z.enum(["monthly", "yearly"]);
export type BillingCycle = z.infer<typeof billingCycleSchema>;

/** Trạng thái subscription nhóm. */
export const subscriptionStatusSchema = z.enum(["active", "past_due", "canceled", "trialing"]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

/** priceMinor trên wire = string chữ số (BigInt-safe, no-float). */
const priceMinorSchema = z
  .string()
  .regex(/^\d+$/, "priceMinor phải là chuỗi số nguyên không âm (minor units, no-float)");

/** entitlements: key → number | boolean | null (null = PENDING chưa cấu hình limit). */
const entitlementsSchema = z.record(z.union([z.number(), z.boolean(), z.null()]));

// ============================================================
// Plan
// ============================================================

export const PLAN_DESCRIPTION_MAX = 500;

/**
 * 1 gói trong catalog. priceMinor "0" = PENDING PO (chưa định giá).
 *
 * US-AE-IMP2-T14-B (@UC9163 plan-multicycle):
 *  - Multi-cycle: 1 `code` có nhiều dòng theo `billingCycle` (monthly + yearly) — khoá composite
 *    (code, billingCycle). Cùng code khác cycle = gói khác giá (KHÔNG trùng).
 *  - `description` = mô tả gói (tùy chọn). `imageKey` = OBJECT KEY R2 (KHÔNG URL — presigned hết hạn +
 *    lộ). `imageUrl` = URL phục vụ presigned resolve từ imageKey lúc GET (null khi chưa upload).
 */
export const planSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    priceMinor: priceMinorSchema,
    currency: z.string(),
    billingCycle: billingCycleSchema,
    entitlements: entitlementsSchema,
    imageKey: z.string().nullable(),
    imageUrl: z.string().nullable(),
    active: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict();

export type Plan = z.infer<typeof planSchema>;

export const planListResponseSchema = z
  .object({
    data: z.array(planSchema),
    pageInfo: z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }).strict(),
  })
  .strict();

export type PlanListResponse = z.infer<typeof planListResponseSchema>;

export const PLAN_CODE_MAX = 40;
export const PLAN_NAME_MAX = 120;

/**
 * Body upsert gói (POST /admin/billing/plans, SUPER_ADMIN). Khoá upsert = (code, billingCycle) — 1 code
 * nhiều cycle (@UC9163 plan-multicycle). priceMinor string số (default "0" PENDING). entitlements value
 * có thể null (PENDING). `description` tùy chọn (null/empty → xóa mô tả). `.strict()`.
 *
 * imageKey KHÔNG nhận qua body này — ảnh upload qua PUT /plans/:code/:cycle/image (multipart) rồi lưu
 * OBJECT KEY riêng (KHÔNG cho client ghi key tùy ý → tránh trỏ object lạ). Upsert KHÔNG clobber imageKey.
 */
export const upsertPlanSchema = z
  .object({
    code: z.string().trim().min(1).max(PLAN_CODE_MAX),
    name: z.string().trim().min(1).max(PLAN_NAME_MAX),
    description: z.string().trim().max(PLAN_DESCRIPTION_MAX).nullable().optional(),
    priceMinor: priceMinorSchema.default("0"),
    currency: z.string().trim().min(1).max(8).default("VND"),
    billingCycle: billingCycleSchema.default("monthly"),
    entitlements: entitlementsSchema.default({}),
    active: z.boolean().default(true),
  })
  .strict();

export type UpsertPlanInput = z.infer<typeof upsertPlanSchema>;

/** Response upload ảnh gói (PUT /plans/:code/:cycle/image). imageUrl = presigned GET (TTL hữu hạn). */
export const planImageUploadResponseSchema = z
  .object({ imageUrl: z.string() })
  .strict();

export type PlanImageUploadResponse = z.infer<typeof planImageUploadResponseSchema>;

// ============================================================
// Group subscription
// ============================================================

/** Subscription của 1 nhóm. groupId/planId = con trỏ. */
export const groupSubscriptionSchema = z
  .object({
    id: z.string(),
    groupId: z.string(),
    planId: z.string(),
    planCode: z.string().nullable(), // resolve nhẹ (hiển thị) — null nếu plan mất
    status: subscriptionStatusSchema,
    startedAt: z.string(),
    currentPeriodEnd: z.string().nullable(),
  })
  .strict();

export type GroupSubscription = z.infer<typeof groupSubscriptionSchema>;

/** Body gán/đổi gói (PATCH /admin/groups/:id/subscription, SUPER_ADMIN). */
export const patchSubscriptionSchema = z
  .object({
    planId: z.string().uuid(),
    status: subscriptionStatusSchema.optional(),
    currentPeriodEnd: z.string().datetime({ offset: true }).nullable().optional(),
  })
  .strict();

export type PatchSubscriptionInput = z.infer<typeof patchSubscriptionSchema>;

// ============================================================
// Entitlement result (@UC9132) — soft-block gate
// ============================================================

/**
 * Kết quả kiểm entitlement 1 nhóm. `breached` = đã vượt limit (soft — KHÔNG chặn cứng/xoá data);
 * `upgradeCta` = tín hiệu app render CTA nâng cấp. limits = key→limit (null = PENDING → KHÔNG breach).
 */
export const entitlementResultSchema = z
  .object({
    groupId: z.string(),
    planCode: z.string().nullable(),
    limits: entitlementsSchema,
    usage: z.record(z.number()), // key → giá trị hiện tại (vd fund_count)
    breached: z.boolean(),
    upgradeCta: z.boolean(),
  })
  .strict();

export type EntitlementResult = z.infer<typeof entitlementResultSchema>;

// ============================================================
// List query
// ============================================================

export const PLAN_LIST_MAX_LIMIT = 100;

export const planListQuerySchema = z
  .object({
    // Review M1: KHÔNG z.coerce.boolean() — Boolean("false")===true (mọi chuỗi non-empty truthy) →
    // ?active=false lọc SAI thành true. Dùng enum "true"|"false" → transform (chỉ "true" = true).
    active: z
      .enum(["true", "false"])
      .transform((v) => v === "true")
      .optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(PLAN_LIST_MAX_LIMIT).optional(),
  })
  .strict();

export type PlanListQuery = z.infer<typeof planListQuerySchema>;
